// Dialog to edit narrative docs for a scientific name (overview / diagnosis / differential diagnosis)
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Divider,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import supabase from "../../../../utils/supabase";
import { useToast } from "../Consoles/Framework/ToastProvider";
import { useSupabaseSession } from "../../../../utils/AuthSessionProvider";
import { DEFAULT_DOC_LANG, SCIENTIFIC_NAME_DOC_TYPES } from "../../../../constants/scientificNameDocs";

const LANG_OPTIONS = ["ja", "en", "zh", "es", "fr", "ru"];

export default function DialogScientificNameDocs({ open, scientificName, onClose }) {
  const toast = useToast();
  const { user } = useSupabaseSession();

  const initialState = useMemo(
    () => Object.fromEntries(SCIENTIFIC_NAME_DOC_TYPES.map((d) => [d.key, ""])),
    []
  );

  const [lang, setLang] = useState(DEFAULT_DOC_LANG);
  const [values, setValues] = useState(initialState);
  const [existingIds, setExistingIds] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const docRefs = useRef({});

  const sanitizeHtml = (html = "") => {
    const fallback = (s = "") =>
      s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    if (typeof window === "undefined" || typeof DOMParser === "undefined") {
      return fallback(html);
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(html || "", "text/html");
    const allowedTags = new Set(["b", "strong", "i", "em", "u", "p", "br", "ul", "ol", "li", "blockquote", "a"]);
    const walk = (node) => {
      const children = Array.from(node.childNodes);
      for (const child of children) {
        if (child.nodeType === 1) {
          const tag = child.tagName.toLowerCase();
          if (!allowedTags.has(tag)) {
            child.replaceWith(...Array.from(child.childNodes));
            continue;
          }
          Array.from(child.attributes).forEach((attr) => {
            const name = attr.name.toLowerCase();
            if (tag === "a" && name === "href") {
              const href = child.getAttribute("href") || "";
              if (!/^https?:\/\//i.test(href) && !href.startsWith("/researcher/")) {
                child.removeAttribute("href");
              } else {
                child.setAttribute("rel", "noreferrer");
                child.setAttribute("target", "_blank");
              }
            } else {
              child.removeAttribute(attr.name);
            }
          });
          walk(child);
        }
      }
    };
    walk(doc.body);
    return doc.body.innerHTML;
  };

  useEffect(() => {
    if (scientificName?.id) {
      setLang((prev) => (LANG_OPTIONS.includes(prev) ? prev : DEFAULT_DOC_LANG));
    }
  }, [scientificName?.id]);

  useEffect(() => {
    const fetchDocs = async () => {
      if (!open || !scientificName?.id) return;
      setLoading(true);
      setError(null);
      const langSafe = (lang || DEFAULT_DOC_LANG).trim() || DEFAULT_DOC_LANG;
      const { data, error: err } = await supabase
        .from("scientific_name_docs")
        .select("id, doc_type, lang, body")
        .eq("scientific_name_id", scientificName.id)
        .eq("lang", langSafe);
      if (err) {
        setError(err.message);
        setValues(initialState);
        setExistingIds({});
      } else {
        const nextValues = { ...initialState };
        const nextIds = {};
        (data || []).forEach((row) => {
          nextValues[row.doc_type] = sanitizeHtml(row.body || "");
          nextIds[row.doc_type] = row.id;
        });
        setValues(nextValues);
        setExistingIds(nextIds);
      }
      setLoading(false);
    };
    fetchDocs();
  }, [open, scientificName?.id, lang, initialState]);

  const handleRichChange = (key, html) => {
    setValues((prev) => ({ ...prev, [key]: sanitizeHtml(html) }));
  };

  const applyCommand = (key, command, value = null) => {
    const el = docRefs.current[key];
    if (!el) return;
    el.focus();
    document.execCommand(command, false, value);
    handleRichChange(key, el.innerHTML);
  };

  const handleSave = async () => {
    if (!scientificName?.id) return;
    setSaving(true);
    setError(null);
    try {
      const langSafe = (lang || DEFAULT_DOC_LANG).trim() || DEFAULT_DOC_LANG;
      const rowsToUpsert = [];
      const idsToDelete = [];
      SCIENTIFIC_NAME_DOC_TYPES.forEach(({ key }) => {
        const body = sanitizeHtml(values[key] || "").trim();
        if (body) {
          rowsToUpsert.push({
            scientific_name_id: scientificName.id,
            doc_type: key,
            lang: langSafe,
            body,
            updated_by: user?.id || null,
          });
        } else if (existingIds[key]) {
          idsToDelete.push(existingIds[key]);
        }
      });

      if (rowsToUpsert.length) {
        const { error: upsertError } = await supabase
          .from("scientific_name_docs")
          .upsert(rowsToUpsert, { onConflict: "scientific_name_id,doc_type,lang" });
        if (upsertError) throw upsertError;
      }
      if (idsToDelete.length) {
        const { error: deleteError } = await supabase
          .from("scientific_name_docs")
          .delete()
          .in("id", idsToDelete);
        if (deleteError) throw deleteError;
      }

      toast?.push("success", "Saved");
      onClose?.(true);
    } catch (e) {
      console.warn("[DialogScientificNameDocs] save failed", e);
      setError(e.message || "Save failed");
      toast?.push("error", e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose?.(false)} maxWidth="md" fullWidth>
      <DialogTitle>Notes for {scientificName?.name_spell_valid || scientificName?.id}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ flex: 1 }}>
              Language
            </Typography>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="doc-lang-label">Language</InputLabel>
              <Select
                labelId="doc-lang-label"
                label="Language"
                value={lang}
                onChange={(e) => setLang(e.target.value)}
              >
                {LANG_OPTIONS.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code.toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            空欄のまま保存すると該当言語の本文を削除します。改行もそのまま保存されます。
          </Typography>
          {error && (
            <Box sx={{ color: "error.main", fontSize: 14 }}>
              {error}
            </Box>
          )}
          <Divider />
          <Stack spacing={2}>
            {SCIENTIFIC_NAME_DOC_TYPES.map((d) => (
              <Box key={d.key}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ flex: 1 }}>
                    {d.fallbackTitle}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Button size="small" variant="outlined" onClick={() => applyCommand(d.key, "bold")} disabled={loading}>
                      Bold
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => applyCommand(d.key, "italic")} disabled={loading}>
                      Italic
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => applyCommand(d.key, "underline")} disabled={loading}>
                      Underline
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => applyCommand(d.key, "insertUnorderedList")} disabled={loading}>
                      • List
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => applyCommand(d.key, "formatBlock", "blockquote")} disabled={loading}>
                      Quote
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const url = prompt("Link URL (https://...)");
                        if (url) applyCommand(d.key, "createLink", url);
                      }}
                      disabled={loading}
                    >
                      Link
                    </Button>
                  </Stack>
                </Stack>
                <Box
                  ref={(el) => {
                    if (el) {
                      docRefs.current[d.key] = el;
                      if (el.innerHTML !== values[d.key]) {
                        el.innerHTML = values[d.key] || "";
                      }
                    }
                  }}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={(e) => handleRichChange(d.key, e.currentTarget.innerHTML)}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 1,
                    minHeight: 120,
                    backgroundColor: (theme) => theme.palette.background.paper,
                    color: (theme) => theme.palette.text.primary,
                    "& a": { color: "inherit" },
                    "&:focus": { outline: "2px solid", outlineColor: "primary.main" },
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  保存時には基本的な装飾のみ許可されます（bold/italic/underline/list/blockquote/link）。
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose?.(false)} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
